/**
 * Video reels — enhances blocks/video-reels.liquid.
 *
 * Liquid renders the cards, posters and the dialog shell; this module adds the Swiper rail, one
 * muted preview and the lightbox. Nothing video-sized loads until it is needed: only the one card
 * that is previewing has a video source attached (released again when it stops), and each card's
 * player markup sits in an inert <template> that is cloned into the dialog only when that video is
 * opened. The lightbox uses a tap surface plus its own mute and captions controls.
 *
 * Analytics: dispatches `video-reels:start`, `video-reels:progress` and `video-reels:complete` on
 * document for assets/google-events.js. Clicks are tracked through the markup's data-click-* hooks.
 */

const ROOT_SELECTOR = '[data-video-reels]';
const PREVIEW_SECONDS = 6;
const PROGRESS_MARKS = [25, 50, 75];
const SWIPE_DISTANCE = 60;
const MUTED_KEY = 'yise:video-reels-muted';
const CAPTIONS_KEY = 'yise:captions-on';
const HOLD_DELAY_MS = 200;
const HOLD_MOVE_TOLERANCE = 10;
const FAST_FORWARD_RATE = 2;
const FEED_SETTLE_MS = 180;
const WARM_DELAY_MS = 600;
const WARM_MP4_BYTES = 1500000;
const probeVideo = document.createElement('video');

const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
const overlayLayout = window.matchMedia('(max-width: 989px)');
const instances = new Map();

const resizeObserver = new ResizeObserver((entries) => {
  entries.forEach((entry) => {
    const instance = instances.get(entry.target.closest(ROOT_SELECTOR));
    if (instance) instance.scheduleCarousel();
  });
});

function readMuted() {
  try {
    return window.sessionStorage.getItem(MUTED_KEY) === 'true';
  } catch (error) {
    return false;
  }
}

function writeMuted(muted) {
  try {
    window.sessionStorage.setItem(MUTED_KEY, String(muted));
  } catch (error) {
    /* Storage can be unavailable in private modes; the preference then lasts for this page. */
  }
}

function captionsPreferred() {
  try {
    const stored = window.sessionStorage.getItem(CAPTIONS_KEY);
    if (stored !== null) return stored === '1';
  } catch (error) {
    /* Private mode still gets the accessible mobile default. */
  }
  return overlayLayout.matches;
}

function prefersLessData() {
  return Boolean(navigator.connection && navigator.connection.saveData);
}

function canWarm() {
  const connection = navigator.connection;
  return !prefersLessData() && !(connection && /(^|-)2g$/.test(connection.effectiveType || ''));
}

/* The source the browser itself would pick: the first <source> whose type it can play. */
function playableSource(item) {
  const template = item && item.querySelector('[data-video-reels-template]');
  const sources = template ? Array.from(template.content.querySelectorAll('[data-video-reels-video] source')) : [];
  const source = sources.find((candidate) => probeVideo.canPlayType(candidate.getAttribute('type') || '') !== '');
  return source ? { url: new URL(source.getAttribute('src'), document.baseURI).href, type: source.getAttribute('type') || '' } : null;
}

function firstMediaUri(playlist, baseUrl) {
  const line = playlist.split('\n').map((entry) => entry.trim()).find((entry) => entry && !entry.startsWith('#'));
  return line ? new URL(line, baseUrl).href : '';
}

/* The variant a native HLS player loads first. Safari's starts on the first listed variant; other
   native players start on the lowest bandwidth and climb. */
const startsOnFirstVariant = 'webkitShowPlaybackTargetPicker' in HTMLVideoElement.prototype;

function startingVariantUri(master, baseUrl) {
  if (startsOnFirstVariant) return firstMediaUri(master, baseUrl);
  const lines = master.split('\n').map((entry) => entry.trim());
  let lowest = null;
  lines.forEach((line, i) => {
    const bandwidth = /^#EXT-X-STREAM-INF:.*?\bBANDWIDTH=(\d+)/.exec(line);
    const uri = bandwidth && lines.slice(i + 1).find((entry) => entry && !entry.startsWith('#'));
    if (uri && (!lowest || Number(bandwidth[1]) < lowest.bandwidth)) lowest = { bandwidth: Number(bandwidth[1]), uri };
  });
  return lowest ? new URL(lowest.uri, baseUrl).href : firstMediaUri(master, baseUrl);
}

function whenSwiperReady() {
  if (window.Swiper) return Promise.resolve(window.Swiper);
  return new Promise((resolve) => {
    const settle = () => {
      if (window.Swiper) resolve(window.Swiper);
    };
    window.addEventListener('yise:swiper-ready', settle, { once: true });
    if (window.yiseThemeScriptsReady && typeof window.yiseThemeScriptsReady.then === 'function') {
      window.yiseThemeScriptsReady.then(settle, () => {});
    }
  });
}

/* Previews wait for the page to finish loading and then for an idle moment, so they never compete
   with the page's own images, fonts and scripts. */
function whenIdleAfterLoad() {
  const idle = () => new Promise((resolve) => {
    if ('requestIdleCallback' in window) {
      window.requestIdleCallback(() => resolve(), { timeout: 2500 });
    } else {
      window.setTimeout(resolve, 1200);
    }
  });
  if (document.readyState === 'complete') return idle();
  return new Promise((resolve) => window.addEventListener('load', resolve, { once: true })).then(idle);
}

class VideoReels {
  constructor(root) {
    this.root = root;
    this.controller = new AbortController();
    this.track = root.querySelector('.video-reels_track');
    this.items = Array.from(root.querySelectorAll('[data-video-reels-item]'));
    this.arrows = root.querySelector('.video-reels_arrows');
    this.prevArrow = root.querySelector('.js-video-reels-prev');
    this.nextArrow = root.querySelector('.js-video-reels-next');
    this.dialog = root.querySelector('[data-video-reels-dialog]');
    this.previewMode = root.getAttribute('data-preview') || 'off';
    this.autoplayNext = root.getAttribute('data-autoplay-next') === 'true';
    this.muted = readMuted();
    this.index = -1;
    this.visibleItems = new Set();
    this.activeItem = null;
    this.pointerItem = null;
    this.previewsReady = false;
    this.lastArrow = null;
    this.lastArrowAt = 0;
  }

  connect() {
    const { signal } = this.controller;

    this.items.forEach((item, index) => {
      const trigger = item.querySelector('[data-video-reels-open]');
      if (!trigger) return;
      trigger.disabled = false;
      if (this.dialog) {
        trigger.setAttribute('aria-controls', this.dialog.id);
        trigger.setAttribute('aria-expanded', 'false');
        trigger.setAttribute('data-dialog-trigger', '');
      }
      trigger.addEventListener('click', () => this.open(index, trigger), { signal });
    });

    this.connectDialog();
    this.connectCarousel();
    this.connectPreviews();
  }

  disconnect() {
    this.disconnected = true;
    this.controller.abort();
    this.teardownVideo();
    if (this.feedObserver) this.feedObserver.disconnect();
    if (this.previewObserver) this.previewObserver.disconnect();
    if (this.track) resizeObserver.unobserve(this.track);
    cancelAnimationFrame(this.carouselFrame);
    cancelAnimationFrame(this.arrowFrame);
    cancelAnimationFrame(this.previewFrame);
    this.stopPreview();
    this.destroyCarousel();
    if (this.dialog && this.dialog.open) this.close({ restoreFocus: false });
  }

  /* ---------------------------------------------------------------- carousel */

  async connectCarousel() {
    if (!this.track) return;
    const { signal } = this.controller;

    [this.prevArrow, this.nextArrow].forEach((button) => {
      if (!button) return;
      const remember = () => {
        this.lastArrow = button;
        this.lastArrowAt = performance.now();
      };
      button.addEventListener('pointerdown', remember, { signal });
      button.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') remember();
      }, { signal });
    });

    this.Swiper = await whenSwiperReady();
    if (this.disconnected || !this.Swiper) return;
    this.buildCarousel();
    resizeObserver.observe(this.track);
  }

  scheduleCarousel() {
    cancelAnimationFrame(this.carouselFrame);
    this.carouselFrame = requestAnimationFrame(() => this.buildCarousel());
  }

  buildCarousel() {
    if (!this.Swiper || this.disconnected) return;

    if (this.track.swiper) {
      this.track.swiper.update();
      if (this.track.swiper.isLocked) this.destroyCarousel();
      return;
    }

    const overflows = this.track.scrollWidth - this.track.clientWidth > 1;
    if (!overflows) {
      if (this.arrows) this.arrows.hidden = true;
      return;
    }

    const sync = (swiper) => this.syncCarousel(swiper);
    new this.Swiper(this.track, {
      slidesPerView: 'auto',
      spaceBetween: 0,
      speed: reducedMotion.matches ? 0 : 400,
      loop: false,
      watchOverflow: true,
      watchSlidesProgress: true,
      passiveListeners: true,
      centeredSlides: false,
      cssMode: false,
      threshold: 10,
      touchAngle: 75,
      touchMoveStopPropagation: true,
      mousewheel: { releaseOnEdges: false, forceToAxis: true, sensitivity: 0.5 },
      a11y: { enabled: false },
      navigation: { prevEl: this.prevArrow, nextEl: this.nextArrow },
      on: {
        init: sync,
        slideChange: sync,
        transitionEnd: sync,
        resize: sync,
        update: sync,
        toEdge: sync,
        fromEdge: sync,
      },
    });
  }

  destroyCarousel() {
    if (!this.track || !this.track.swiper) return;
    this.track.swiper.destroy(true, true);
    this.track.querySelectorAll('.video-reels_slide').forEach((slide) => {
      slide.inert = false;
      slide.removeAttribute('aria-hidden');
    });
    if (this.arrows) this.arrows.hidden = true;
  }

  syncCarousel(swiper) {
    const locked = swiper.isLocked;
    if (this.arrows) this.arrows.hidden = locked;

    [
      [this.prevArrow, swiper.isBeginning],
      [this.nextArrow, swiper.isEnd],
    ].forEach(([button, atEdge]) => {
      if (!button) return;
      button.setAttribute('aria-disabled', atEdge || locked ? 'true' : 'false');
    });

    cancelAnimationFrame(this.arrowFrame);
    this.arrowFrame = requestAnimationFrame(() => {
      [this.prevArrow, this.nextArrow].forEach((button) => {
        if (!button) return;
        button.removeAttribute('disabled');
        button.setAttribute('tabindex', '0');
      });
      const recentlyUsed = this.lastArrow && performance.now() - this.lastArrowAt < 1000;
      const focusLost = !document.activeElement || document.activeElement === document.body;
      if (recentlyUsed && focusLost && this.lastArrow.isConnected) {
        this.lastArrow.focus({ preventScroll: true });
      }
    });

    const stripRect = swiper.el.getBoundingClientRect();
    const rects = swiper.slides.map((slide) => slide.getBoundingClientRect());
    swiper.slides.forEach((slide, index) => {
      const visible = rects[index].left >= stripRect.left - 1 && rects[index].right <= stripRect.right + 1;
      slide.inert = !visible;
      slide.setAttribute('aria-hidden', visible ? 'false' : 'true');
    });
  }

  reveal(slide) {
    if (!this.track || !this.track.swiper) return;
    const index = this.track.swiper.slides.indexOf(slide);
    if (index > -1) this.track.swiper.slideTo(index);
  }

  /* ---------------------------------------------------------------- previews
     One preview plays at a time: the card being hovered or focused, otherwise the first card fully
     in view. Only that card has a video source attached; every other card is just its poster. */

  previewsAllowed() {
    return this.previewMode !== 'off' && !reducedMotion.matches && !prefersLessData();
  }

  async connectPreviews() {
    if (this.previewMode === 'off') return;
    const { signal } = this.controller;

    this.items.forEach((item) => {
      const preview = item.querySelector('[data-video-reels-preview]');
      if (!preview) return;

      preview.addEventListener('timeupdate', () => {
        if (preview.currentTime > PREVIEW_SECONDS) preview.currentTime = 0;
      }, { signal });
      preview.addEventListener('playing', () => item.setAttribute('data-previewing', ''), { signal });
      preview.addEventListener('pause', () => item.removeAttribute('data-previewing'), { signal });

      const enter = () => {
        this.pointerItem = item;
        this.schedulePreview();
      };
      const leave = () => {
        if (this.pointerItem !== item) return;
        this.pointerItem = null;
        this.schedulePreview();
      };
      item.addEventListener('pointerenter', enter, { signal });
      item.addEventListener('pointerleave', leave, { signal });
      item.addEventListener('focusin', enter, { signal });
      item.addEventListener('focusout', (event) => {
        if (!item.contains(event.relatedTarget)) leave();
      }, { signal });
    });

    reducedMotion.addEventListener('change', () => this.schedulePreview(), { signal });
    document.addEventListener('visibilitychange', () => this.schedulePreview(), { signal });

    this.previewObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          this.visibleItems.add(entry.target);
        } else {
          this.visibleItems.delete(entry.target);
        }
      });
      this.schedulePreview();
    }, { threshold: 0.75 });
    this.items.forEach((item) => {
      if (item.querySelector('[data-video-reels-preview]')) this.previewObserver.observe(item);
    });

    await whenIdleAfterLoad();
    if (this.disconnected) return;
    this.previewsReady = true;
    this.schedulePreview();
  }

  schedulePreview() {
    cancelAnimationFrame(this.previewFrame);
    this.previewFrame = requestAnimationFrame(() => this.updatePreview());
  }

  choosePreview() {
    if (!this.previewsReady || !this.previewsAllowed()) return null;
    if (document.hidden || (this.dialog && this.dialog.open)) return null;
    if (this.pointerItem && this.pointerItem.querySelector('[data-video-reels-preview]')) return this.pointerItem;
    if (this.previewMode !== 'in_view') return null;
    return this.items.find((item) => this.visibleItems.has(item)) || null;
  }

  updatePreview() {
    if (this.disconnected) return;
    const next = this.choosePreview();
    if (next !== this.activeItem) {
      this.stopPreview();
      if (next) this.startPreview(next);
    }
  }

  startPreview(item) {
    const preview = item.querySelector('[data-video-reels-preview]');
    if (!preview) return;
    this.activeItem = item;
    if (!preview.getAttribute('src') && preview.dataset.src) preview.src = preview.dataset.src;
    preview.muted = true;
    const played = preview.play();
    if (played && typeof played.catch === 'function') played.catch(() => {});
  }

  stopPreview() {
    const item = this.activeItem;
    this.activeItem = null;
    const preview = item && item.querySelector('[data-video-reels-preview]');
    if (!preview) return;
    preview.pause();
    this.releasePreview(preview);
  }

  /* Drops the preview's media so a card that is not previewing holds no decoded frames or buffer. */
  releasePreview(preview) {
    if (!preview.getAttribute('src')) return;
    preview.removeAttribute('src');
    preview.load();
  }

  /* ---------------------------------------------------------------- dialog
     The lightbox is a vertical feed with one view per video, snapping as the shopper scrolls or
     swipes. Views are built once, on first open, and show a poster; only the view in the middle of
     the screen gets its video (released again when it scrolls away), and product cards render for
     that view and its neighbours so nothing pops in while scrolling. */

  connectDialog() {
    const dialog = this.dialog;
    if (!dialog) return;
    const { signal } = this.controller;

    this.feed = dialog.querySelector('[data-video-reels-feed]');
    this.viewTemplate = dialog.querySelector('[data-video-reels-view-template]');
    this.status = dialog.querySelector('[data-video-reels-status]');
    this.prevButton = dialog.querySelector('[data-video-reels-step="-1"]');
    this.nextButton = dialog.querySelector('[data-video-reels-step="1"]');
    this.views = [];
    this.lastFeedScroll = 0;
    this.videoTimer = 0;
    this.direction = 1;
    this.warmTimer = 0;
    this.warmController = null;
    this.warmItem = null;
    this.warmed = new WeakSet();

    /* A flick through the feed passes several views. Starting a stream for each one leaves Safari
       finishing abandoned HLS loads before the video the shopper lands on, so a view only gets its
       player once the feed has stopped moving. */
    this.feed?.addEventListener('scroll', () => {
      this.lastFeedScroll = performance.now();
    }, { passive: true, signal });

    dialog.addEventListener('close', () => this.onClose(), { signal });

    dialog.addEventListener('click', (event) => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      if (target === dialog || target === this.feed || target.matches('[data-video-reels-view]')) {
        this.close();
        return;
      }
      if (target.closest('[data-video-reels-products-toggle]')) {
        this.setProductsCollapsed(!dialog.hasAttribute('data-products-collapsed'));
        return;
      }
      if (target.closest('form[action*="/cart/add"] [type="submit"]')) {
        /* Close before the form submits so the cart drawer the add opens is not hidden behind the
           modal. The form stays in the (closed) dialog, so the cart's submit listener still sees it. */
        this.close({ restoreFocus: false });
      }
    }, { signal });

    [this.prevButton, this.nextButton].forEach((button) => {
      if (!button) return;
      button.addEventListener('click', () => this.step(Number(button.getAttribute('data-video-reels-step'))), { signal });
    });

    dialog.addEventListener('keydown', (event) => {
      if (event.key === 'Escape') {
        /* The native dialog closes itself; stop page-level Escape handlers (chat and signup
           widgets) from reacting as well and pulling focus away from the card. */
        event.stopPropagation();
        return;
      }
      if (event.defaultPrevented || event.altKey || event.ctrlKey || event.metaKey) return;
      const target = event.target;
      if (target instanceof Element && target.matches('input, select, textarea, video')) return;
      if (event.key === 'ArrowUp' || event.key === 'PageUp') {
        event.preventDefault();
        this.step(-1);
      } else if (event.key === 'ArrowDown' || event.key === 'PageDown') {
        event.preventDefault();
        this.step(1);
      }
    }, { signal });
  }

  buildViews() {
    if (this.views.length || !this.feed || !this.viewTemplate) return;
    const shell = this.viewTemplate.content.firstElementChild;
    if (!shell) return;

    this.views = this.items.map((item, index) => {
      const view = shell.cloneNode(true);
      view.dataset.index = String(index);
      view.inert = true;
      const poster = view.querySelector('[data-video-reels-view-poster]');
      const url = this.posterFor(item);
      if (poster && url) {
        poster.src = url;
      } else if (poster) {
        poster.remove();
      }
      const name = view.querySelector('[data-video-reels-view-name]');
      if (name) name.textContent = item.getAttribute('data-name') || '';
      this.feed.append(view);
      return view;
    });

    this.feedObserver = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) this.activate(Number(entry.target.dataset.index));
      });
    }, { root: this.feed, threshold: 0.6 });
    this.views.forEach((view) => this.feedObserver.observe(view));
  }

  /* The card's own poster is already in the browser cache, so a view opens on a frame at once. */
  posterFor(item) {
    const card = item.querySelector('.video-reels_poster');
    if (card && card.complete && (card.currentSrc || card.src)) return card.currentSrc || card.src;
    const template = item.querySelector('[data-video-reels-template]');
    const video = template && template.content.querySelector('video[poster]');
    return video ? video.getAttribute('poster') : '';
  }

  /* Cloned details can repeat (the page's own product appears under every video), so every id,
     label target and radio group gets a per-view suffix. */
  uniquify(root, suffix) {
    root.querySelectorAll('[id]').forEach((element) => {
      element.id = `${element.id}${suffix}`;
    });
    root.querySelectorAll('[for]').forEach((element) => {
      element.setAttribute('for', `${element.getAttribute('for')}${suffix}`);
    });
    root.querySelectorAll('[aria-controls]').forEach((element) => {
      element.setAttribute('aria-controls', `${element.getAttribute('aria-controls')}${suffix}`);
    });
    root.querySelectorAll('input[type="radio"][name]').forEach((element) => {
      element.name = `${element.name}${suffix}`;
    });
  }

  setProductsCollapsed(collapsed) {
    this.dialog.toggleAttribute('data-products-collapsed', collapsed);
    this.dialog.querySelectorAll('[data-video-reels-products-toggle]').forEach((toggle) => {
      toggle.setAttribute('aria-expanded', String(!collapsed));
      toggle.setAttribute('aria-label', collapsed ? toggle.dataset.labelShow : toggle.dataset.labelHide);
    });
  }

  renderDetails(index) {
    const view = this.views[index];
    const item = this.items[index];
    if (!view || !item || view.hasAttribute('data-details-ready')) return;
    view.setAttribute('data-details-ready', '');

    const info = view.querySelector('[data-video-reels-view-info]');
    const template = item.querySelector('[data-video-reels-template]');
    const details = template && template.content.querySelector('[data-video-reels-part="details"]');
    if (!info) return;

    const fragment = document.createDocumentFragment();
    if (details) fragment.append(...details.cloneNode(true).childNodes);
    const fallback = this.root.querySelector('[data-video-reels-fallback]');
    if (fallback && !fragment.querySelector('.video-reels_products')) {
      fragment.append(fallback.content.cloneNode(true));
    }
    this.uniquify(fragment, `-v${index}`);
    info.replaceChildren(fragment);

    this.connectProductCarousel(info);

    if (window.YiseProductUpsellCard && typeof window.YiseProductUpsellCard.connect === 'function') {
      window.YiseProductUpsellCard.connect(info);
    }
  }

  connectProductCarousel(root) {
    const carousel = root.querySelector('[data-video-reels-products]');
    const track = carousel && carousel.querySelector('[data-video-reels-product-track]');
    const slides = track ? Array.from(track.querySelectorAll('.video-reels_product-slide')) : [];
    if (!track || slides.length < 2) return;

    const previous = carousel.querySelector('[data-video-reels-product-step="-1"]');
    const next = carousel.querySelector('[data-video-reels-product-step="1"]');
    let index = 0;

    const offsetOf = (slide) => slide.offsetLeft - slides[0].offsetLeft;

    const sync = () => {
      let nearest = Infinity;
      slides.forEach((slide, i) => {
        const distance = Math.abs(offsetOf(slide) - track.scrollLeft);
        if (distance < nearest) {
          nearest = distance;
          index = i;
        }
      });
    };

    const step = (direction) => {
      index = (index + direction + slides.length) % slides.length;
      track.scrollTo({ left: offsetOf(slides[index]), behavior: reducedMotion.matches ? 'auto' : 'smooth' });
      sync();
    };

    previous?.addEventListener('click', () => step(-1), { signal: this.controller.signal });
    next?.addEventListener('click', () => step(1), { signal: this.controller.signal });
    track.addEventListener('scroll', sync, { passive: true, signal: this.controller.signal });
    sync();
  }

  renderVideo(index) {
    const view = this.views[index];
    const item = this.items[index];
    const template = item && item.querySelector('[data-video-reels-template]');
    const media = template && template.content.querySelector('[data-video-reels-part="media"]');
    const player = view && view.querySelector('[data-video-reels-player]');
    const controlsSlot = view && view.querySelector('[data-video-reels-video-controls-slot]');
    if (!media || !player || !controlsSlot) return;

    const fragment = media.cloneNode(true);
    fragment.querySelectorAll('.video-reels_overlay-name').forEach((element) => element.remove());
    const video = fragment.querySelector('[data-video-reels-video]');
    const mediaAction = fragment.querySelector('[data-video-reels-media-action]');
    const fastForwardBadge = fragment.querySelector('[data-video-reels-fast-forward]');
    const captionDisplay = fragment.querySelector('[data-video-reels-caption-display]');
    const controls = fragment.querySelector('[data-video-reels-video-controls]');
    if (!video || !mediaAction || !controls) return;

    const poster = this.posterFor(item);
    if (poster) video.setAttribute('poster', poster);
    controls.remove();
    const mediaNodes = Array.from(fragment.childNodes);
    player.append(...mediaNodes);
    controlsSlot.replaceChildren(controls);

    this.video = video;
    this.videoMounts = [...mediaNodes, controls];
    this.videoPlayer = player;
    this.videoControlsSlot = controlsSlot;
    this.startedCurrent = false;
    this.marks = new Set();
    this.videoController = new AbortController();
    const { signal } = this.videoController;

    const muteButton = controls.querySelector('[data-video-reels-mute]');
    const captionsButton = controls.querySelector('[data-video-reels-captions]');
    const volumeOnIcon = muteButton && muteButton.querySelector('[data-video-reels-volume-on]');
    const volumeOffIcon = muteButton && muteButton.querySelector('[data-video-reels-volume-off]');
    const captionTracks = Array.from(video.textTracks).filter((track) => track.kind === 'captions' || track.kind === 'subtitles');
    let captionsEnabled = captionTracks.some((track) => track.mode === 'showing');
    let holdTimer = 0;
    let fastForwarding = false;
    let previousPlaybackRate = 1;
    let pointerState = null;
    let keyboardKey = '';
    let suppressClickUntil = 0;

    const togglePlayback = () => {
      if (video.paused) {
        const played = video.play();
        if (played && typeof played.catch === 'function') played.catch(() => {});
      } else {
        video.pause();
      }
    };

    const renderCaptions = () => {
      if (!captionDisplay) return;
      const text = captionTracks.flatMap((track) => Array.from(track.activeCues || []).map((cue) => {
        if (typeof cue.getCueAsHTML === 'function') return cue.getCueAsHTML().textContent || '';
        return cue.text || '';
      })).filter(Boolean).join('\n');
      captionDisplay.textContent = text;
      captionDisplay.hidden = !captionsEnabled || !text;
    };

    const syncControls = () => {
      mediaAction.setAttribute('aria-label', video.paused ? mediaAction.dataset.labelPlay : mediaAction.dataset.labelPause);
      if (muteButton) {
        const label = video.muted ? muteButton.dataset.labelUnmute : muteButton.dataset.labelMute;
        muteButton.setAttribute('aria-label', label);
        muteButton.setAttribute('aria-pressed', String(video.muted));
        if (volumeOnIcon) volumeOnIcon.hidden = video.muted;
        if (volumeOffIcon) volumeOffIcon.hidden = !video.muted;
      }
      if (captionsButton) {
        captionsButton.setAttribute('aria-pressed', String(captionsEnabled));
        captionsButton.setAttribute('aria-label', captionsEnabled ? captionsButton.dataset.labelOff : captionsButton.dataset.labelOn);
      }
    };

    const setCaptions = (enabled, persist = false) => {
      captionsEnabled = Boolean(enabled && captionTracks.length);
      captionTracks.forEach((track) => {
        track.mode = captionsEnabled ? 'hidden' : 'disabled';
      });
      if (persist) {
        try {
          window.sessionStorage.setItem(CAPTIONS_KEY, captionsEnabled ? '1' : '0');
        } catch (error) {
          /* Private mode keeps the preference for the current video. */
        }
      }
      renderCaptions();
      syncControls();
    };

    const clearHoldTimer = () => {
      window.clearTimeout(holdTimer);
      holdTimer = 0;
    };

    const startFastForward = () => {
      holdTimer = 0;
      if (video.paused) return;
      previousPlaybackRate = video.playbackRate;
      video.playbackRate = FAST_FORWARD_RATE;
      fastForwarding = true;
      player.setAttribute('data-fast-forwarding', '');
      if (fastForwardBadge) fastForwardBadge.hidden = false;
      if (pointerState && typeof mediaAction.setPointerCapture === 'function') {
        try {
          mediaAction.setPointerCapture(pointerState.id);
        } catch (error) {
          /* The pointer can finish before capture is applied. */
        }
      }
    };

    const scheduleFastForward = () => {
      clearHoldTimer();
      holdTimer = window.setTimeout(startFastForward, HOLD_DELAY_MS);
    };

    const endFastForward = ({ suppressClick = false } = {}) => {
      clearHoldTimer();
      const pointerId = pointerState && pointerState.id;
      pointerState = null;
      keyboardKey = '';
      if (fastForwarding) video.playbackRate = previousPlaybackRate;
      fastForwarding = false;
      player.removeAttribute('data-fast-forwarding');
      if (fastForwardBadge) fastForwardBadge.hidden = true;
      if (suppressClick) suppressClickUntil = performance.now() + 600;
      if (pointerId !== null && pointerId !== undefined && typeof mediaAction.hasPointerCapture === 'function' && mediaAction.hasPointerCapture(pointerId)) {
        mediaAction.releasePointerCapture(pointerId);
      }
    };

    this.endFastForward = endFastForward;

    mediaAction.addEventListener('pointerdown', (event) => {
      if (!event.isPrimary || event.button !== 0) return;
      endFastForward();
      pointerState = { id: event.pointerId, x: event.clientX, y: event.clientY, cancelled: false };
      scheduleFastForward();
    }, { signal });
    mediaAction.addEventListener('pointermove', (event) => {
      if (!pointerState || pointerState.id !== event.pointerId || pointerState.cancelled) return;
      if (Math.hypot(event.clientX - pointerState.x, event.clientY - pointerState.y) <= HOLD_MOVE_TOLERANCE) return;
      pointerState.cancelled = true;
      clearHoldTimer();
    }, { signal });
    mediaAction.addEventListener('pointerup', (event) => {
      if (!pointerState || pointerState.id !== event.pointerId) return;
      endFastForward({ suppressClick: fastForwarding || pointerState.cancelled });
    }, { signal });
    mediaAction.addEventListener('pointercancel', () => endFastForward(), { signal });
    mediaAction.addEventListener('lostpointercapture', () => endFastForward(), { signal });
    mediaAction.addEventListener('click', (event) => {
      if (performance.now() < suppressClickUntil) {
        event.preventDefault();
        return;
      }
      togglePlayback();
    }, { signal });
    mediaAction.addEventListener('keydown', (event) => {
      if ((event.key !== 'Enter' && event.key !== ' ') || event.repeat) return;
      event.preventDefault();
      keyboardKey = event.key;
      suppressClickUntil = performance.now() + 600;
      scheduleFastForward();
    }, { signal });
    mediaAction.addEventListener('keyup', (event) => {
      if (!keyboardKey || event.key !== keyboardKey) return;
      event.preventDefault();
      const wasFastForwarding = fastForwarding;
      endFastForward({ suppressClick: true });
      if (!wasFastForwarding) togglePlayback();
    }, { signal });
    mediaAction.addEventListener('blur', () => endFastForward(), { signal });
    muteButton?.addEventListener('click', () => {
      video.muted = !video.muted;
    }, { signal });
    captionsButton?.addEventListener('click', () => {
      setCaptions(!captionsEnabled, true);
    }, { signal });

    let buffering = false;
    const syncLoading = () => {
      player.toggleAttribute('data-loading', !video.paused && (buffering || video.readyState < HTMLMediaElement.HAVE_FUTURE_DATA));
    };
    video.addEventListener('waiting', () => {
      buffering = true;
      syncLoading();
    }, { signal });
    ['playing', 'canplay', 'seeked'].forEach((type) => video.addEventListener(type, () => {
      buffering = false;
      syncLoading();
    }, { signal }));
    ['play', 'pause', 'emptied', 'error'].forEach((type) => video.addEventListener(type, syncLoading, { signal }));

    video.muted = this.muted;
    video.addEventListener('playing', syncControls, { signal });
    video.addEventListener('pause', syncControls, { signal });
    video.addEventListener('play', () => {
      if (this.startedCurrent) return;
      this.startedCurrent = true;
      this.emit('start');
    }, { signal });
    video.addEventListener('playing', () => this.scheduleWarm(index), { once: true, signal });
    video.addEventListener('timeupdate', () => this.onTimeUpdate(), { signal });
    video.addEventListener('ended', () => {
      endFastForward();
      this.onEnded();
    }, { signal });
    video.addEventListener('volumechange', () => {
      this.muted = video.muted;
      writeMuted(this.muted);
      syncControls();
    }, { signal });
    captionTracks.forEach((track) => track.addEventListener('cuechange', renderCaptions, { signal }));
    document.addEventListener('visibilitychange', () => {
      if (document.hidden) endFastForward();
    }, { signal });

    if (video.readyState > 0) {
      setCaptions(captionsPreferred());
    } else {
      video.addEventListener('loadedmetadata', () => {
        setCaptions(captionsPreferred());
      }, { once: true, signal });
    }

    syncControls();
    const played = video.play();
    if (played && typeof played.catch === 'function') played.catch(syncLoading);
    syncLoading();
  }

  activate(index) {
    const view = this.views[index];
    if (!view || index === this.index) return;

    this.teardownVideo();
    const previous = this.views[this.index];
    if (previous) previous.inert = true;

    if (this.index >= 0) this.direction = index > this.index ? 1 : -1;
    if (this.warmItem && this.warmItem !== this.items[index]) this.cancelWarm();
    this.index = index;
    view.inert = false;
    [index, index + 1, index - 1].forEach((position) => this.renderDetails(position));
    [index + 1, index - 1].forEach((position) => {
      const poster = this.views[position] && this.views[position].querySelector('[data-video-reels-view-poster]');
      if (poster) poster.loading = 'eager';
    });
    this.queueVideo(index);
    this.syncSteps();
    this.announce();
  }

  open(index, opener) {
    const dialog = this.dialog;
    if (!dialog || typeof dialog.showModal !== 'function') return;
    this.stopPreview();
    this.buildViews();
    if (!dialog.open) {
      const openedWithSharedDialog = window.YiseDialog?.openDialog(dialog, { opener });
      if (!openedWithSharedDialog) {
        this.fallbackOpener = opener || null;
        this.restoreFallbackFocus = true;
        dialog.showModal();
      }
    }
    const view = this.views[index];
    if (!view) return;
    this.feed.scrollTop = view.offsetTop;
    this.index = -1;
    this.activate(index);
  }

  close({ restoreFocus = true } = {}) {
    if (!this.dialog || !this.dialog.open) return;
    if (window.YiseDialog?.closeDialog(this.dialog, { restoreFocus })) return;
    this.restoreFallbackFocus = restoreFocus;
    this.dialog.close();
  }

  step(direction) {
    const target = this.views[this.index + direction];
    if (!target) return;
    target.scrollIntoView({ behavior: reducedMotion.matches ? 'auto' : 'smooth', block: 'start' });
  }

  queueVideo(index) {
    window.clearTimeout(this.videoTimer);
    this.videoTimer = 0;
    const wait = FEED_SETTLE_MS - (performance.now() - this.lastFeedScroll);
    if (wait <= 0) {
      this.renderVideo(index);
      return;
    }
    this.videoTimer = window.setTimeout(() => {
      this.videoTimer = 0;
      if (this.index === index && this.dialog.open) this.queueVideo(index);
    }, wait);
  }

  /* Once the current video plays, fetch the start of the next one in the direction of travel —
     its HLS playlists and first segment, or the head of its MP4 — into the HTTP cache the player
     reads from, so the next swipe starts almost at once. */
  scheduleWarm(index) {
    window.clearTimeout(this.warmTimer);
    if (!canWarm()) return;
    this.warmTimer = window.setTimeout(() => {
      this.warmTimer = 0;
      if (this.index === index && this.dialog.open) this.warm(this.items[index + this.direction]);
    }, WARM_DELAY_MS);
  }

  async warm(item) {
    if (!item || this.warmed.has(item) || this.warmItem === item) return;
    const source = playableSource(item);
    if (!source) return;

    this.cancelWarm();
    const controller = new AbortController();
    this.warmController = controller;
    this.warmItem = item;
    const request = (url, headers) => fetch(url, { signal: controller.signal, priority: 'low', headers });

    try {
      if (/mpegurl/i.test(source.type)) {
        const master = await (await request(source.url)).text();
        const variantUrl = startingVariantUri(master, source.url);
        const variant = variantUrl ? await (await request(variantUrl)).text() : '';
        const segmentUrl = variant ? firstMediaUri(variant, variantUrl) : '';
        if (segmentUrl) await (await request(segmentUrl)).arrayBuffer();
      } else {
        await (await request(source.url, { Range: `bytes=0-${WARM_MP4_BYTES - 1}` })).arrayBuffer();
      }
      this.warmed.add(item);
    } catch (error) {
      /* Aborted or offline: the player loads the video normally when it is reached. */
    } finally {
      if (this.warmController === controller) {
        this.warmController = null;
        this.warmItem = null;
      }
    }
  }

  cancelWarm() {
    window.clearTimeout(this.warmTimer);
    this.warmTimer = 0;
    if (this.warmController) this.warmController.abort();
    this.warmController = null;
    this.warmItem = null;
  }

  teardownVideo() {
    window.clearTimeout(this.videoTimer);
    this.videoTimer = 0;
    window.clearTimeout(this.warmTimer);
    this.warmTimer = 0;
    if (this.endFastForward) this.endFastForward();
    this.endFastForward = null;
    if (this.videoController) this.videoController.abort();
    this.videoController = null;
    const video = this.video;
    this.video = null;
    if (video) {
      video.preload = 'none';
      video.pause();
      video.removeAttribute('src');
      video.querySelectorAll('source').forEach((source) => source.remove());
      video.load();
    }
    (this.videoMounts || []).forEach((element) => element.remove());
    this.videoMounts = [];
    if (this.videoPlayer) this.videoPlayer.removeAttribute('data-loading');
    this.videoPlayer = null;
    if (this.videoControlsSlot) this.videoControlsSlot.replaceChildren();
    this.videoControlsSlot = null;
  }

  onClose() {
    this.teardownVideo();
    this.cancelWarm();
    const view = this.views[this.index];
    if (view) view.inert = true;
    this.index = -1;
    const fallbackOpener = this.fallbackOpener;
    this.fallbackOpener = null;
    if (this.restoreFallbackFocus && fallbackOpener instanceof HTMLElement && fallbackOpener.isConnected) {
      fallbackOpener.focus({ preventScroll: true });
    }
    this.restoreFallbackFocus = false;
    this.schedulePreview();
  }

  onTimeUpdate() {
    const video = this.video;
    if (!video || !Number.isFinite(video.duration) || video.duration <= 0) return;
    const percent = (video.currentTime / video.duration) * 100;
    PROGRESS_MARKS.forEach((mark) => {
      if (percent >= mark && !this.marks.has(mark)) {
        this.marks.add(mark);
        this.emit('progress', { percent: mark });
      }
    });
  }

  onEnded() {
    this.emit('complete', { percent: 100 });
    if (this.autoplayNext && this.dialog.open) this.step(1);
  }

  syncSteps() {
    if (this.prevButton) this.prevButton.setAttribute('aria-disabled', this.index <= 0 ? 'true' : 'false');
    if (this.nextButton) {
      this.nextButton.setAttribute('aria-disabled', this.index >= this.items.length - 1 ? 'true' : 'false');
    }
  }

  announce() {
    if (!this.status) return;
    const item = this.items[this.index];
    const template = this.dialog.getAttribute('data-status-template') || '';
    this.status.textContent = template
      .replace('[index]', String(this.index + 1))
      .replace('[total]', String(this.items.length))
      .replace('[name]', (item && item.getAttribute('data-name')) || '');
  }

  emit(name, extra = {}) {
    const item = this.items[this.index];
    const video = this.video;
    document.dispatchEvent(new CustomEvent(`video-reels:${name}`, {
      detail: {
        videoTitle: (item && item.getAttribute('data-name')) || '',
        videoKey: (item && item.getAttribute('data-video-key')) || '',
        playlist: this.root.getAttribute('data-playlist') || '',
        position: this.index + 1,
        total: this.items.length,
        duration: video && Number.isFinite(video.duration) ? Math.round(video.duration) : 0,
        currentTime: video ? Math.round(video.currentTime) : 0,
        ...extra,
      },
    }));
  }
}

function connect(root) {
  if (instances.has(root)) return;
  const instance = new VideoReels(root);
  instances.set(root, instance);
  instance.connect();
}

function disconnect(root) {
  const instance = instances.get(root);
  if (!instance) return;
  instance.disconnect();
  instances.delete(root);
}

function scan(scope) {
  instances.forEach((instance, root) => {
    if (!root.isConnected) disconnect(root);
  });
  scope.querySelectorAll(ROOT_SELECTOR).forEach(connect);
}

document.addEventListener('shopify:section:load', (event) => scan(event.target));

document.addEventListener('shopify:section:unload', (event) => {
  event.target.querySelectorAll(ROOT_SELECTOR).forEach(disconnect);
});

document.addEventListener('shopify:block:select', (event) => {
  const target = event.target;
  if (!(target instanceof Element)) return;
  const slide = target.closest('.video-reels_slide');
  const root = slide && slide.closest(ROOT_SELECTOR);
  const instance = root && instances.get(root);
  if (instance) instance.reveal(slide);
});

scan(document);
